// React Dependencies
import React, { Component } from 'react';

// External Dependencies
import moment from 'moment';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
  FormGroup,
  withStyles,
} from '@material-ui/core';

// Internal Dependencies
import OverLoading from '../global/OverLoading';
import { Status } from '../../common/constants';
import { formatTag, StringToInteger, Trim } from '../../common/util';
import { IfFalseThrowError, IfMapCoordInvalidThrowError, IsEmpty, IsString, IsValidHttpsUrl } from '../../common/checks';
import apiService from '../../services.js/apiService';
import MText from '../common/MText';
import Tagging from '../common/tagging';
import MapModal from './MapModal';
import Colours from '../../styles/colours';
import MButton from '../common/MButton';
import MaterialDatePicker from '../global/form/MaterialDatePicker';
import PoiMap from '../common/poi/poiMap';
import PageSuggestionField from '../common/suggestions/PageSuggestionField';

class PoiInfo extends Component {
  constructor(props) {
    super(props);
    this.selectCategoryRef = null;
    this.dropZoneRef = null;
    this.state = {
      id:            null,
      loading:       false,
      isUpdate:      false,
      redirect:      false,
      initialStatus: Status.Draft,
      showMapModal:  false,

      // linked page states
      selectedPage: null,

      formData: {
        pageId:          null,
        name:            '',
        description:     '',
        type:            '',
        address:         '',
        lat:             null,
        lng:             null,
        tel:             '',
        website:         '',
        openingHours:    null,
        media:           null,
        status:          Status.Draft,
        rating:          null,
        reviewCount:     null,
        isWheelChairAcc: false,
        isPetFriendly:   false,
        svcDogFriendly:  null,
        isPermanent:     null,
        freq:            null,
        startDate:       null,
        endDate:         null,
        tags:            [],
      },
    };
    this.minLogoRes = 200;
    this.maxLogoRes = 2000;

    this.openMapModal = this.openMapModal.bind(this);
    this.closeMapModal = this.closeMapModal.bind(this);
  }

  async fetchPoiData() {
    try {
      const poi = await apiService.getPoi(this.props.match.params.id);
      if (poi) {
        this.setState({
          formData:      { ...poi },
          initialStatus: poi.status,
        }, () => {
          if (this.state.isUpdate && !IsEmpty(this.state.formData.pageId)) {
            this.fetchPageData(this.state.formData.pageId);
          }
        });
      } else {
        toast.warn('POI not found');
      }
    } catch (error) {
      toast.error(error.message);
    }
  }

  componentDidMount() {
    if (this.props.match && this.props.match.params && this.props.match.params.id) {
      this.setState({ isUpdate: true });
      this.fetchPoiData();
    }
  }

  fetchPageData = async (pageId) => {
    const pageData = await apiService.getPageById(pageId);
    if (pageData) {
      this.setState(() => ({
        selectedPage: { id: pageId, name: pageData.name },
      }));
    }
  }

  openMapModal() {
    this.setState({ showMapModal: true });
  }

  closeMapModal() {
    this.setState({ showMapModal: false });
  }

  onChange = (e) => {
    this.setState((prevState) => ({
      formData: {
        ...prevState.formData,
        [e.target.name]: e.target.value,
      },
    }));
  };

  isDeactivated = () => this.state.initialStatus === Status.Deactivated

  onSubmit = async (e) => {
    try {
      document.activeElement.blur();
      e.preventDefault();
      this.setState({ loading: true });
      let poi = null;

      // validate location coordinates
      IfMapCoordInvalidThrowError(this.state.formData.lat, this.state.formData.lng);

      for (const key of Object.keys(this.state.formData)) {
        if (!IsEmpty(this.state.formData[key])) {
          if (key === 'tel') {
            const tel = this.state.formData[key].replace(/-| /g, '');
            const telRegex = /^\d{6,13}$/;
            IfFalseThrowError(telRegex.test(tel), `"${key}" must be a valid telephone format`);
            this.setState((prevState) => ({
              formData: {
                ...prevState.formData,
                [key]: tel,
              },
            }));
          }

          if (key === 'rating') {
            const ratingRegex = /^[0-5](\.\d{1,2})?$/;

            const ratingValue = parseFloat(this.state.formData[key]);

            IfFalseThrowError(
              ratingRegex.test(this.state.formData[key])
              && ratingValue >= 0
              && ratingValue <= 5,
              `"${key}" must be a valid rating between 0.00 and 5.00 (decimals optional)`,
            );
          }

          if (key === 'reviewCount') {
            const numericValue = StringToInteger(this.state.formData[key]);
            IfFalseThrowError(!isNaN(numericValue), `"${key}" must be a valid number`);
          }

          if (key === 'website') {
            const trimmedLink = Trim(this.state.formData[key], false, false);
            if (trimmedLink !== '') {
              const isHttpsUrl = IsValidHttpsUrl(trimmedLink);
              IfFalseThrowError(isHttpsUrl, `URL for "${key}" must be a valid https URL`);
            }
          }
        }
      }

      const name = Trim(this.state.formData.name, false, true);
      const lat = IsEmpty(this.state.formData.lat) ? null : this.state.formData.lat;
      const lng = IsEmpty(this.state.formData.lng) ? null : this.state.formData.lng;
      const pageId = IsEmpty(this.state.selectedPage) ? null : this.state.selectedPage.id;
      const startDate = !IsEmpty(this.state.formData.startDate)
        ? moment(this.state.formData.startDate).utcOffset('+08').startOf('day').format()
        : null;
      const endDate = !IsEmpty(this.state.formData.endDate)
        ? moment(this.state.formData.endDate).utcOffset('+08').endOf('day').format()
        : null;

      if (this.state.isUpdate) {
        const payload = {
          lat,
          lng,
          name,
          pageId,
          startDate,
          endDate,
          description:     this.state.formData.description,
          type:            this.state.formData.type,
          address:         this.state.formData.address,
          tel:             this.state.formData.tel,
          website:         this.state.formData.website,
          status:          this.state.formData.status,
          openingHours:    this.state.formData.openingHours,
          media:           this.state.formData.media,
          isWheelChairAcc: this.state.formData.isWheelChairAcc,
          isPetFriendly:   this.state.formData.isPetFriendly,
          svcDogFriendly:  this.state.formData.svcDogFriendly,
          isPermanent:     this.state.formData.isPermanent,
          freq:            this.state.formData.freq,
          tags:            this.state.formData.tags,
        };

        poi = await apiService.updatePoi(this.props.match.params.id, payload);
        toast.success('POI updated');
        this.setState({ loading: false, formData: poi, initialStatus: poi.status });
      } else {
        const payload = {
          ...this.state.formData,
          name,
          pageId,
          startDate,
          endDate,
        };

        poi = await apiService.addPoi(payload);
        toast.success('POI added');
        this.setState({ loading: false, redirect: true, id: poi.id, formData: poi, initialStatus: poi.status });
      }
    } catch (err) {
      this.setState({ loading: false });
      toast.error(IsString(err) ? err : err.message);
    }
  };

  preventSubmitOnEnter = (e) => {
    // we need both preventing default + checking keyCode to prevent submitting
    const { keyCode, target } = e;
    if (keyCode !== 13 || target instanceof HTMLTextAreaElement) {
      return;
    }
    e.preventDefault();
  }

  preventWhitespace = (e) => {
    e.target.value = e.target.value.replace(' ', '');
  };

  render() {
    if (this.state.redirect) {
      return (
        <Redirect to={`/admin/poi/${this.state.id}`} />
      );
    }

    const mandatoryTags = [];

    if (!IsEmpty(this.state.formData.name)) {
      mandatoryTags.push(formatTag(this.state.formData.name));
    }

    return (
      <div>
        <form
          className="form position-relative"
          onSubmit={(e) => this.onSubmit(e)}
          autoComplete="off"
        >
          {this.state.loading && <OverLoading />}
          <div className="row">
            <div className="col-lg-6 col-sm-12 form-section">
              <MText text="General Information" size={20} bold />
              <div className="form-group mt-2">
                <MText text="Name" required />
                <input
                  type="text"
                  className="form-control"
                  name="name"
                  value={this.state.formData.name}
                  onChange={(e) => this.onChange(e)}
                  required
                  disabled={this.state.isUpdate || this.isDeactivated()}
                />
                <MText text="*required" colour={Colours.Red} size={10} className="ml-1" />
              </div>

              <div className="form-group">
                <MText text="Status" />
                <RadioGroup
                  row
                  aria-label="status"
                  name="status"
                  value={this.state.formData.status}
                  onChange={(e) => this.onChange(e)}
                >
                  <FormControlLabel
                    value={Status.Draft}
                    control={<Radio color="default" />}
                    label={Status.Draft}
                    disabled={(this.state.isUpdate && this.isDeactivated())}
                  />
                  {
                    this.state.isUpdate && (
                      <>
                        <FormControlLabel
                          value={Status.Live}
                          control={<Radio color="primary" />}
                          label={Status.Live}
                          disabled={(this.state.isUpdate && this.isDeactivated())}
                        />
                        <FormControlLabel
                          value={Status.Deactivated}
                          control={<Radio color="primary" />}
                          label={Status.Deactivated}
                        />
                      </>
                    )
                  }
                </RadioGroup>
              </div>

              <div className="form-group">
                <MText text="Linked Page" />
                <PageSuggestionField
                  disabled={this.isDeactivated()}
                  value={this.state.selectedPage}
                  onChange={(value) => this.setState({ selectedPage: value })}
                />
              </div>

              <div className="form-group">
                <MText text="Description" />
                <textarea
                  className="form-control"
                  name="description"
                  value={this.state.formData.description}
                  onChange={(e) => this.onChange(e)}
                  disabled={this.isDeactivated()}
                />
              </div>

              <div className="row">
                <div className="col-md-4 mb-3">
                  <MaterialDatePicker
                    minDate={false}
                    label="Start Date"
                    style={{ marginTop: 0, marginBottom: 24 }}
                    disabled={this.isDeactivated()}
                    value={IsEmpty(this.state.formData.startDate) ? null : new Date(this.state.formData.startDate)}
                    onChange={(date) => this.setState((prevState) => ({
                      formData: {
                        ...prevState.formData,
                        startDate: date,
                      },
                    }))}
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <MaterialDatePicker
                    minDate={IsEmpty(this.state.formData.startDate) ? false : new Date(this.state.formData.startDate)}
                    label="End Date"
                    style={{ marginTop: 0, marginBottom: 24 }}
                    disabled={this.isDeactivated()}
                    value={IsEmpty(this.state.formData.endDate) ? null : new Date(this.state.formData.endDate)}
                    onChange={(date) => this.setState((prevState) => ({
                      formData: {
                        ...prevState.formData,
                        endDate: date,
                      },
                    }))}
                  />
                </div>
              </div>

              {/* <div className="form-group">
                <MText text="Type" />
                <input
                  type="text"
                  className="form-control"
                  name="type"
                  value={this.state.formData.type}
                  onChange={(e) => this.onChange(e)}
                  disabled={this.isDeactivated()}
                />
              </div> */}

              <div className="form-group">
                <MText text="Address" />
                <input
                  type="text"
                  className="form-control"
                  name="address"
                  value={this.state.formData.address}
                  onChange={(e) => this.onChange(e)}
                  disabled={this.isDeactivated()}
                />
              </div>

              <div className="form-group">
                <MText text="Rating" />
                <input
                  className="form-control"
                  name="rating"
                  value={this.state.formData.rating}
                  disabled
                />
              </div>

              <div className="form-group">
                <MText text="Review Count" />
                <input
                  className="form-control"
                  name="reviewCount"
                  value={this.state.formData.reviewCount}
                  disabled
                />
              </div>

              <div className="form-group">
                <MText text="Telephone" />
                <input
                  type="text"
                  className="form-control"
                  name="tel"
                  value={this.state.formData.tel}
                  onChange={(e) => this.onChange(e)}
                  disabled={this.isDeactivated()}
                />
              </div>

              <div className="form-group">
                <MText text="Frequency" />

                <input
                  type="text"
                  className="form-control mb-2"
                  placeholder="Monthly / Yearly / One-time"
                  value={this.state.formData.freq && this.state.formData.freq.value ? this.state.formData.freq.value : ''}
                  onChange={(e) => this.setState((prevState) => ({
                    formData: {
                      ...prevState.formData,
                      freq: {
                        ...prevState.formData.freq,
                        value: e.target.value,
                      },
                    },
                  }))}
                  disabled={this.isDeactivated()}
                />

                <textarea
                  type="text"
                  className="form-control"
                  placeholder="Additional info"
                  value={this.state.formData.freq && this.state.formData.freq.info ? this.state.formData.freq.info : ''}
                  onChange={(e) => this.setState((prevState) => ({
                    formData: {
                      ...prevState.formData,
                      freq: {
                        ...prevState.formData.freq,
                        info: e.target.value,
                      },
                    },
                  }))}
                  disabled={this.isDeactivated()}
                />
              </div>

              <div className="form-group">
                <FormGroup>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={this.state.formData.isWheelChairAcc}
                        onChange={(e) => this.setState((prevState) => ({
                          formData: {
                            ...prevState.formData,
                            isWheelChairAcc: e.target.checked,
                          },
                        }))}
                        disabled={this.isDeactivated()}
                      />
                    )}
                    label="Wheelchair Accessible"
                  />
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={this.state.formData.isPetFriendly}
                        onChange={(e) => this.setState((prevState) => ({
                          formData: {
                            ...prevState.formData,
                            isPetFriendly: e.target.checked,
                          },
                        }))}
                        disabled={this.isDeactivated()}
                      />
                    )}
                    label="Pet Friendly"
                  />
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={this.state.formData.svcDogFriendly}
                        onChange={(e) => this.setState((prevState) => ({
                          formData: {
                            ...prevState.formData,
                            svcDogFriendly: e.target.checked,
                          },
                        }))}
                        disabled={this.isDeactivated()}
                      />
                    )}
                    label="Service Dog Friendly"
                  />
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={!!this.state.formData.isPermanent}
                        onChange={(e) => this.setState((prevState) => ({
                          formData: {
                            ...prevState.formData,
                            isPermanent: e.target.checked,
                          },
                        }))}
                        disabled={this.isDeactivated()}
                      />
                    )}
                    label="Permanent"
                  />
                </FormGroup>
              </div>

              <div className="form-group">
                <MText text="Website" />
                <input
                  className="form-control"
                  name="website"
                  placeholder="www.example.com"
                  value={this.state.formData.website}
                  onChange={(e) => this.onChange(e)}
                  disabled={this.isDeactivated()}
                />
              </div>

              <div className="form-group">
                <div className="form-group">
                  <MText text="Tags" />
                  <Tagging
                    textSize="h6"
                    disabled={this.isDeactivated()}
                    onKeyDown={(e) => this.preventSubmitOnEnter(e)}
                    data={[...this.state.formData.tags].map((tag) => ({ name: tag, removable: mandatoryTags.includes(tag) }))}
                    onChange={(values) => {
                      const formattedUserTags = values.map((value) => formatTag(value));
                      this.setState((prevState) => ({
                        formData: {
                          ...prevState.formData,
                          tags: formattedUserTags,
                        },
                      }));
                    }}
                  />
                </div>
              </div>

              <MText text="Location" size={20} bold />
              {
                this.state.initialStatus !== Status.Deactivated && (
                  <MButton
                    text="Pick location from map"
                    variant="text"
                    className="px-0 pt-0"
                    textColour={Colours.Blue}
                    textStyle={{ fontWeight: 500, textDecoration: 'underline'  }}
                    onClick={this.openMapModal}
                  />
                )
              }

              <div className="d-lg-none mt-2">
                <PoiMap
                  mapHeight={250}
                  coord={{
                    lat: this.state.formData.lat,
                    lng: this.state.formData.lng,
                  }}
                  isPoiMarker
                  disabled
                />
              </div>

              <div className="form-group mt-2">
                <MText text="Lat" />
                <input
                  type="text"
                  className="form-control"
                  name="lat"
                  value={this.state.formData.lat}
                  onChange={(e) => this.onChange(e)}
                  onInput={this.preventWhitespace}
                  disabled={this.isDeactivated()}
                />
              </div>

              <div className="form-group">
                <MText text="Lng" />
                <input
                  type="text"
                  className="form-control"
                  name="lng"
                  value={this.state.formData.lng}
                  onChange={(e) => this.onChange(e)}
                  onInput={this.preventWhitespace}
                  disabled={this.isDeactivated()}
                />
              </div>
            </div>

            <div className="d-none d-lg-block col-lg-6">
              <div className={this.props.classes.mapContainer}>
                <PoiMap
                  mapHeight={450}
                  coord={{
                    lat: this.state.formData.lat,
                    lng: this.state.formData.lng,
                  }}
                  isPoiMarker
                  disabled
                />
              </div>

            </div>
          </div>

          <div className="row mt-5">
            <div className="col-lg-2 col-sm-12 offset-lg-4">
              <div className="form-group">
                <input
                  type="submit"
                  disabled={this.state.loading || this.isDeactivated()}
                  className={`btn block ${this.state.loading ? 'btn-secondary' : 'btn-primary'}`}
                  value={this.state.isUpdate ? 'Update' : 'Add'}
                />
              </div>
            </div>
          </div>
        </form>

        <MapModal
          showMapModal={this.state.showMapModal}
          closeMapModal={this.closeMapModal}
          coord={{
            lat: this.state.formData.lat,
            lng: this.state.formData.lng,
          }}
          onApply={(coord) => {
            this.setState((prev) => ({
              formData: {
                ...prev.formData,
                lat: coord.lat,
                lng: coord.lng,
              },
            }));
          }}
        />

      </div>
    );
  }
}

PoiInfo.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
  classes: PropTypes.shape({
    mapContainer: PropTypes.string,
  }).isRequired,
};

export default withStyles(() => ({
  mapContainer: {
    marginTop:                    28,
    width:                        450,
    position:                     'fixed',
    right:                        20,
    '@media (max-width: 1280px)': {
      width: 340,
    },
    '@media (max-width: 1024px)': {
      width: 330,
    },
  },
}))(PoiInfo);
